/* ==========================================================================
   Product page: gallery, zoom, variant selection, engraving counter,
   quantity, add to bag (opens cart drawer), accordions.
   ========================================================================== */
(function () {
  'use strict';

  const root = document.querySelector('[data-product]');
  if (!root) return;

  const data = JSON.parse(root.querySelector('[data-product-json]').textContent);

  /* ---------------------------------------------------------------------- */
  /* Gallery                                                                 */
  /* ---------------------------------------------------------------------- */
  const track = root.querySelector('[data-gallery-track]');
  const slides = track ? Array.prototype.slice.call(track.children) : [];
  const thumbs = root.querySelectorAll('[data-gallery-thumb]');
  const prev = root.querySelector('[data-gallery-prev]');
  const next = root.querySelector('[data-gallery-next]');
  const counter = root.querySelector('[data-gallery-current]');
  let current = 0;

  function goTo(i, smooth) {
    if (!track || !slides.length) return;
    current = Math.max(0, Math.min(slides.length - 1, i));
    track.scrollTo({ left: slides[current].offsetLeft, behavior: smooth === false ? 'instant' : 'smooth' });
    update();
  }

  function update() {
    thumbs.forEach(function (t, n) { t.classList.toggle('is-active', n === current); });
    if (counter) counter.textContent = current + 1;
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === slides.length - 1;
  }

  if (track) {
    let scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        current = Math.round(track.scrollLeft / track.clientWidth);
        update();
      }, 60);
    }, { passive: true });
  }
  if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
  if (next) next.addEventListener('click', function () { goTo(current + 1); });
  thumbs.forEach(function (t) {
    t.addEventListener('click', function () { goTo(parseInt(t.dataset.galleryThumb, 10)); });
  });
  update();

  function goToMedia(mediaId) {
    if (!mediaId) return;
    const idx = slides.findIndex(function (s) { return s.dataset.mediaId === String(mediaId); });
    if (idx > -1) goTo(idx, false);
  }

  /* ---------------------------------------------------------------------- */
  /* Zoom lightbox                                                           */
  /* ---------------------------------------------------------------------- */
  const zoom = root.querySelector('[data-zoom]');
  const zoomImgs = zoom ? Array.prototype.slice.call(zoom.querySelectorAll('[data-zoom-img]')) : [];
  let zoomIndex = 0;
  let zoomTrigger = null;

  function showZoom(i) {
    zoomIndex = (i + zoomImgs.length) % zoomImgs.length;
    zoomImgs.forEach(function (img, n) {
      img.classList.remove('is-zoomed');
      img.hidden = n !== zoomIndex;
      if (n === zoomIndex && !img.src) img.src = img.dataset.src;
    });
  }

  function openZoom(i, trigger) {
    if (!zoom || !zoomImgs.length) return;
    zoomTrigger = trigger;
    zoom.hidden = false;
    document.body.classList.add('is-locked');
    showZoom(i);
    zoom.querySelector('[data-zoom-close]').focus();
  }

  function closeZoom() {
    if (!zoom || zoom.hidden) return;
    zoom.hidden = true;
    document.body.classList.remove('is-locked');
    if (zoomTrigger) zoomTrigger.focus();
  }

  root.querySelectorAll('[data-zoom-open]').forEach(function (btn) {
    btn.addEventListener('click', function () { openZoom(parseInt(btn.dataset.zoomOpen, 10), btn); });
  });

  if (zoom) {
    zoom.querySelector('[data-zoom-close]').addEventListener('click', closeZoom);
    const zp = zoom.querySelector('[data-zoom-prev]');
    const zn = zoom.querySelector('[data-zoom-next]');
    if (zp) zp.addEventListener('click', function () { showZoom(zoomIndex - 1); });
    if (zn) zn.addEventListener('click', function () { showZoom(zoomIndex + 1); });

    // Click the image to zoom in further, at the point clicked; move to pan
    zoomImgs.forEach(function (img) {
      img.addEventListener('click', function (e) {
        if (img.classList.contains('is-zoomed')) {
          img.classList.remove('is-zoomed');
          return;
        }
        setOrigin(img, e);
        img.classList.add('is-zoomed');
      });
      img.addEventListener('mousemove', function (e) {
        if (img.classList.contains('is-zoomed')) setOrigin(img, e);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (zoom.hidden) return;
      if (e.key === 'Escape') closeZoom();
      if (e.key === 'ArrowLeft') showZoom(zoomIndex - 1);
      if (e.key === 'ArrowRight') showZoom(zoomIndex + 1);
    });
  }

  function setOrigin(img, e) {
    const r = img.getBoundingClientRect();
    img.style.transformOrigin = ((e.clientX - r.left) / r.width) * 100 + '% ' + ((e.clientY - r.top) / r.height) * 100 + '%';
  }

  /* ---------------------------------------------------------------------- */
  /* Variant selection                                                       */
  /* ---------------------------------------------------------------------- */
  const form = root.querySelector('[data-product-form]');
  const idInput = root.querySelector('[data-variant-id]');
  const priceEl = root.querySelector('.product-info__price[data-price]');
  const buy = root.querySelector('[data-buy]');
  const waitlist = root.querySelector('[data-waitlist]');
  const variantEnquire = root.querySelector('[data-variant-enquire]');
  const waitlistNote = root.querySelector('[data-waitlist-note]');
  const optionBlocks = root.querySelectorAll('[data-option-index]');

  /* Price-upon-enquiry form: drops open under the Enquire button */
  const enquiryPanel = root.querySelector('[data-product-enquiry]');
  const enquiryToggles = root.querySelectorAll('[data-enquiry-toggle]');
  const enquiryOptions = root.querySelector('[data-enquiry-options]');
  const customiseBtn = root.querySelector('[data-customise]');
  const enquiryType = root.querySelector('[data-enquiry-type]');
  let enquiryMode = 'enquire'; // 'enquire' (price upon enquiry) or 'customise'

  function setEnquiry(open) {
    if (!enquiryPanel) return;
    enquiryPanel.hidden = !open;
    if (!open) enquiryToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    root.classList.toggle('is-enquiring', open);
  }

  function updateEnquiryOptions() {
    if (!enquiryOptions || !idInput) return;
    const v = data.variants.find(function (x) { return String(x.id) === String(idInput.value); });
    const parts = [];
    if (v && v.title && v.title !== 'Default Title') parts.push(v.title.split(' / ').join(', '));
    const rs = root.querySelector('[data-ring-size-input]:checked');
    if (rs) parts.push('Ring size: ' + rs.value);
    enquiryOptions.value = parts.join(' · ');
  }

  enquiryToggles.forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!enquiryPanel) return;
      const mode = btn.hasAttribute('data-customise') ? 'customise' : 'enquire';
      const opening = enquiryPanel.hidden || mode !== enquiryMode;
      enquiryMode = mode;
      if (enquiryType) enquiryType.value = mode === 'customise' ? 'Customisation request' : 'Product enquiry';
      enquiryToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      updateEnquiryOptions();
      setEnquiry(opening);
      if (opening) btn.setAttribute('aria-expanded', 'true');
      if (opening) {
        const first = enquiryPanel.querySelector('input[name="contact[name]"]');
        if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 60);
      }
    });
  });

  // After sending (page reloads): reopen the panel on the thank-you / error message
  if (enquiryPanel && enquiryPanel.querySelector('[data-enquiry-done]')) {
    setEnquiry(true);
    setTimeout(function () { enquiryPanel.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
  }

  function selectedOptions() {
    const values = [];
    optionBlocks.forEach(function (block) {
      const select = block.querySelector('select[data-option-input]');
      const checked = block.querySelector('input[data-option-input]:checked');
      values.push(select ? select.value : checked ? checked.value : null);
    });
    return values;
  }

  function findVariant(values) {
    return data.variants.find(function (v) {
      return v.options.every(function (opt, i) { return opt === values[i]; });
    });
  }

  // Grey out option values that are sold out. Each option is checked against
  // the choices ABOVE it only (e.g. Size given Metal + Stone), so the option that
  // actually causes the sell-out is the one marked.
  function isAvailable(values, index, value) {
    return data.variants.some(function (v) {
      if (!v.available || v.options[index] !== value) return false;
      for (let i = 0; i < index; i++) if (v.options[i] !== values[i]) return false;
      return true;
    });
  }

  function markAvailability(values) {
    optionBlocks.forEach(function (block, index) {
      block.querySelectorAll('[data-option-input]').forEach(function (input) {
        const opts = input.tagName === 'SELECT' ? Array.prototype.slice.call(input.options) : [input];
        opts.forEach(function (el) {
          const available = isAvailable(values, index, el.value);
          if (el.tagName === 'OPTION') {
            el.textContent = el.value + (available ? '' : ' — sold out');
          } else {
            el.closest('.stone-button').classList.toggle('is-unavailable', !available);
          }
        });
      });
      const label = block.querySelector('[data-option-selected]');
      if (label) label.textContent = (values[index] || '').replace(/ diamond$/i, '');
    });
  }

  function onVariantChange() {
    const values = selectedOptions();
    const variant = findVariant(values);
    markAvailability(values);

    if (!variant) {
      if (buy) buy.hidden = true;
      return;
    }

    idInput.value = variant.id;
    updateEnquiryOptions();
    if (customiseBtn) customiseBtn.hidden = variant.onEnquiry;
    if (!data.enquireOnly && !variant.onEnquiry && enquiryMode === 'enquire') setEnquiry(false);
    if (waitlistNote) waitlistNote.value = 'Waitlist: ' + root.dataset.title + ' — ' + variant.title;

    if (!data.enquireOnly && priceEl) {
      priceEl.innerHTML = variant.onEnquiry
        ? data.enquiryText
        : variant.price + (variant.compare ? ' <s class="product-info__compare">' + variant.compare + '</s>' : '');
    }

    // R0 variants (e.g. Natural diamond): "Price upon enquiry" + Enquire button, no Add to bag
    if (buy) buy.hidden = !variant.available || variant.onEnquiry;
    if (variantEnquire) variantEnquire.hidden = !variant.onEnquiry;
    if (waitlist) waitlist.hidden = variant.available || variant.onEnquiry;

    goToMedia(variant.mediaId);

    // Swap Materials & Specifications to this variant's own specs (if it has any)
    const variantSpecs = root.querySelectorAll('[data-specs-variant]');
    if (variantSpecs.length) {
      let found = false;
      variantSpecs.forEach(function (el) {
        const match = el.dataset.specsVariant === String(variant.id);
        el.hidden = !match;
        if (match) found = true;
      });
      const specsDefault = root.querySelector('[data-specs-default]');
      if (specsDefault) specsDefault.hidden = found;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  if (form) {
    form.addEventListener('change', function (e) {
      if (e.target.hasAttribute('data-option-input')) onVariantChange();
      if (e.target.hasAttribute('data-ring-size-input')) updateEnquiryOptions();
    });
  }
  if (optionBlocks.length) markAvailability(selectedOptions());

  /* ---------------------------------------------------------------------- */
  /* Engraving counter + quantity                                            */
  /* ---------------------------------------------------------------------- */
  const engraving = root.querySelector('[data-engraving]');
  const engravingCount = root.querySelector('[data-engraving-count]');
  if (engraving && engravingCount) {
    engraving.addEventListener('input', function () { engravingCount.textContent = engraving.value.length; });
  }

  const qty = root.querySelector('[data-qty-input]');
  const minus = root.querySelector('[data-qty-minus]');
  const plus = root.querySelector('[data-qty-plus]');
  if (qty && minus && plus) {
    minus.addEventListener('click', function () { qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1); });
    plus.addEventListener('click', function () { qty.value = (parseInt(qty.value, 10) || 1) + 1; });
  }

  /* ---------------------------------------------------------------------- */
  /* Add to bag → stay on page, open cart drawer                             */
  /* ---------------------------------------------------------------------- */
  const addButton = root.querySelector('[data-add-button]');
  const addLabel = root.querySelector('[data-add-label]');
  const addError = root.querySelector('[data-add-error]');

  if (form && addButton) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // Ring size must be chosen before adding to bag
      const ringSize = root.querySelector('[data-ring-size]');
      if (ringSize && !ringSize.querySelector('[data-ring-size-input]:checked')) {
        if (addError) { addError.textContent = 'Please choose your ring size.'; addError.hidden = false; }
        const trig = ringSize.querySelector('[data-lf-trigger]');
        if (trig) { trig.focus(); trig.click(); }
        return;
      }
      const body = new FormData(form);
      if (engraving && !engraving.value.trim()) body.delete('properties[Engraving]');
      body.append('sections', window.LenoraCart ? window.LenoraCart.sectionIds().join(',') : 'cart-drawer');
      body.append('sections_url', window.location.pathname);

      const original = addLabel.textContent;
      addButton.setAttribute('aria-busy', 'true');
      addLabel.textContent = 'Adding…';
      if (addError) addError.hidden = true;

      fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (res) { return res.json(); })
        .then(function (res) {
          if (res.status) throw new Error(res.description || res.message);
          addLabel.textContent = 'Added to bag';
          if (window.LenoraCart) window.LenoraCart.render(res.sections, true);
          setTimeout(function () { addLabel.textContent = original; }, 2000);
        })
        .catch(function (err) {
          addLabel.textContent = original;
          if (addError) {
            addError.textContent = err.message || 'Something went wrong. Please try again.';
            addError.hidden = false;
          }
        })
        .finally(function () {
          addButton.removeAttribute('aria-busy');
        });
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Size guide pop-up                                                       */
  /* ---------------------------------------------------------------------- */
  const sizeGuide = root.querySelector('[data-size-guide]');
  const sizeGuideOpen = root.querySelector('[data-size-guide-open]');
  if (sizeGuide && sizeGuideOpen && typeof sizeGuide.showModal === 'function') {
    sizeGuideOpen.addEventListener('click', function () { sizeGuide.showModal(); });
    sizeGuide.querySelector('[data-size-guide-close]').addEventListener('click', function () { sizeGuide.close(); });
    // Click outside the chart closes it
    sizeGuide.addEventListener('click', function (e) { if (e.target === sizeGuide) sizeGuide.close(); });
  }

  /* ---------------------------------------------------------------------- */
  /* Accordions: opening one closes the others                               */
  /* ---------------------------------------------------------------------- */
  const accordions = root.querySelectorAll('.product-accordions .accordion');
  accordions.forEach(function (acc) {
    acc.addEventListener('toggle', function () {
      if (!acc.open) return;
      accordions.forEach(function (other) { if (other !== acc) other.open = false; });
    });
  });
})();
